import { RefObject, useCallback, useEffect, useRef } from 'react'

type UseFixedTableHeaderOptions = {
  containerRef: RefObject<HTMLDivElement | null>
}

export default function useFixedTableHeader({
  containerRef,
}: UseFixedTableHeaderOptions) {
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const tableHeadRef = useRef<HTMLTableSectionElement>(null)
  const tableHeadRowRef = useRef<HTMLTableRowElement>(null)
  const isIntersectingRef = useRef<boolean>(false)

  const updateHeaderPosition = useCallback(() => {
    const containerEl = containerRef?.current
    const tableContainerEl = tableContainerRef.current
    const tableHeadRowEl = tableHeadRowRef.current
    if (!containerEl || !tableContainerEl || !tableHeadRowEl) return

    const { scrollTop: containerScrollTop } = containerEl
    const {
      offsetTop: tableContainerOffsetTop,
      clientHeight: tableContainerClientHeight,
    } = tableContainerEl
    // We want to set transform when the tableContainerOffsetTop is past the container,
    // but only if it's still in view. (The 50 is to account for the last row.)
    if (
      containerScrollTop > tableContainerOffsetTop &&
      containerScrollTop <
        tableContainerOffsetTop + tableContainerClientHeight - 50
    ) {
      const yTranslation = Math.floor(
        Math.abs(containerScrollTop - tableContainerOffsetTop),
      )
      tableHeadRowEl.style.setProperty(
        'transform',
        `translateY(${yTranslation}px)`,
      )
    } else {
      tableHeadRowEl.style.removeProperty('transform')
    }
  }, [containerRef])

  useEffect(() => {
    const containerEl = containerRef?.current
    const tableHeadEl = tableHeadRef.current
    const tableHeadRowEl = tableHeadRowRef.current

    // The head row is repositioned every frame while it is off screen — the
    // only way to hold it in place against a container that scrolls the whole
    // panel. The frame id keeps that to one chain and lets cleanup stop it; a
    // chain that re-schedules itself has no other way to end.
    let frameId: number | null = null

    const positionUntilVisible = () => {
      if (isIntersectingRef.current) {
        frameId = null
        tableHeadRowEl?.style.removeProperty('transform')
        return
      }
      updateHeaderPosition()
      frameId = requestAnimationFrame(positionUntilVisible)
    }

    const translate = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const { isIntersecting } = entry
        isIntersectingRef.current = isIntersecting
        if (isIntersecting) {
          tableHeadRowEl?.style.removeProperty('transform')
        } else if (frameId === null) {
          frameId = requestAnimationFrame(positionUntilVisible)
        }
      })
    }

    const observer = new IntersectionObserver(translate, {
      root: containerEl,
      threshold: 1,
    })

    if (tableHeadEl) observer.observe(tableHeadEl)

    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId)
      tableHeadRowEl?.style.removeProperty('transform')
      observer.disconnect()
    }
  }, [containerRef, updateHeaderPosition])

  return { tableContainerRef, tableHeadRef, tableHeadRowRef }
}
